/**
 * @fileoverview Input/output schemas for the prose-comparer-v2 agent.
 *
 * This agent evaluates multiple text options across a 14-dimension rubric,
 * comments on writing quality and narrative fidelity for each one, and
 * picks the best.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow.
 */
export const InputSchema = BaseFlowInputSchema
  .merge(AGENT_SCHEMA_OBJECTS.originalPrompt.schema)
  .merge(AGENT_SCHEMA_OBJECTS.evalOptions.schema)
  .merge(AGENT_SCHEMA_OBJECTS.userMessage.schema)
  .merge(AGENT_SCHEMA_OBJECTS.genre.schema)
  .merge(AGENT_SCHEMA_OBJECTS.genreGuidance.schema);

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// RUBRIC
// =============================================================================

/**
 * The 14 rubric criteria used to evaluate each option.
 *
 * The first 8 are positive qualities (higher is better); the last 6 are
 * writing flaws (lower is better).
 */
export const RUBRIC_CRITERIA = [
  // Positive qualities
  'nuanced-characters',
  'emotionally-engaging',
  'compelling-plot',
  'coherent',
  'well-earned-tonal-shifts',
  'characters-consistent-with-profile',
  'followed-chapter-plan',
  'faithful-to-writing-prompt',
  // Writing flaws
  'weak-dialogue',
  'tell-dont-show',
  'unsurprising-or-uncreative',
  'amateurish',
  'purple-prose',
  'forced-poetry-or-metaphor',
] as const;

export const RubricCriterionSchema = z.enum(RUBRIC_CRITERIA);
export type RubricCriterion = z.infer<typeof RubricCriterionSchema>;

export const RubricHighlightSchema = z.object({
  criterion: RubricCriterionSchema.describe('Which rubric dimension this highlight refers to.'),
  sentiment: z.enum(['positive', 'negative']).describe(
    'Whether the option stands out positively or negatively on this criterion.'
  ),
  note: z.string().describe('A short, concrete observation grounded in the option text.'),
});
export type RubricHighlight = z.infer<typeof RubricHighlightSchema>;

/**
 * Per-option evaluation produced for every text option. Comments on writing
 * quality and narrative fidelity are mandatory; rubric highlights only call
 * out dimensions that genuinely stand out.
 */
export const OptionEvaluationSchema = z.object({
  modelName: z.string().describe(
    'The EXACT model name from the option, as shown after "Option from model".'
  ),
  writingQualityComment: z.string().describe(
    'Brief comment on the option\'s overall writing quality, framed against the rubric.'
  ),
  narrativeFidelityComment: z.string().describe(
    'Brief comment on how closely this option follows the narrative requested in the original prompt, chapter plan, and character profiles.'
  ),
  narrativeDeviates: z.boolean().describe(
    'True if this option meaningfully deviates from the narrative the prompt asked for (drifts off-plan, contradicts characters, ignores key events). False if it stays on track.'
  ),
  rubricHighlights: z.array(RubricHighlightSchema).describe(
    'Rubric dimensions that stand out (positively or negatively) for this option. Include only the criteria worth calling out — do not pad with neutral entries.'
  ),
});
export type OptionEvaluation = z.infer<typeof OptionEvaluationSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Output schema for the prose comparer.
 */
export const OutputSchema = z.object({
  winningModelName: z.string().describe(
    'The EXACT model name from the winning option, as shown after "Option from model". Do not include prefixes like "model" or "option" - just the name itself (e.g., "Refined", "Original", "GPT-4").'
  ),
  justification: z.string().describe(
    'A concise but comprehensive explanation of why this option was chosen as the best.'
  ),
  evaluations: z.array(OptionEvaluationSchema).describe(
    'Per-option evaluation for every text option supplied, in the same order.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;
